import * as React from 'react';
import { DayPicker } from 'react-day-picker';
import { bn } from 'react-day-picker/locale';
import { cn } from '@/lib/utils';
import { toBnDigits, toEnDigits } from '@/utils/bnFormat';
import { IconChevronLeft, IconChevronRight, IconChevronDown, IconSearch, IconCheck } from '@tabler/icons-react';

/**
 * Bengali formatters for the calendar.
 *
 * The date-fns Bengali locale translates month and weekday names but still
 * writes numbers with Western digits, so a "Bengali" calendar would read
 * "জানুয়ারি 2026" over a grid of 1-31. These put the digits in Bengali too.
 */
const bnMonth = (month, width = 'wide') =>
    bn.localize.month(typeof month === 'number' ? month : month.getMonth(), { width });

const BN_FORMATTERS = {
    formatCaption: (month) => `${bnMonth(month)} ${toBnDigits(month.getFullYear())}`,
    formatMonthDropdown: (month) => bnMonth(month),
    formatYearDropdown: (year) =>
        toBnDigits(typeof year === 'number' ? year : year.getFullYear()),
    // The header has one 36 px column per day, so the short names ("রবি",
    // "মঙ্গল") rather than the full ones.
    formatWeekdayName: (weekday) => bn.localize.day(weekday.getDay(), { width: 'short' }),
    formatDay: (date) => toBnDigits(date.getDate()),
    formatWeekNumber: (weekNumber) => toBnDigits(weekNumber),
};

/** The same, for the labels a screen reader reads out. */
const bnFullDate = (date) =>
    `${toBnDigits(date.getDate())} ${bnMonth(date)} ${toBnDigits(date.getFullYear())}`;

const BN_LABELS = {
    labelDayButton: (date) => `${bn.localize.day(date.getDay())}, ${bnFullDate(date)}`,
    labelGrid: (date) => `${bnMonth(date)} ${toBnDigits(date.getFullYear())}`,
    labelGridcell: (date) => bnFullDate(date),
};

// ── Custom dropdown (replaces native <select> for month & year) ─────────────
function CalendarDropdown({ value, onChange, options = [], 'aria-label': ariaLabel }) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const triggerRef = React.useRef(null);
  const menuRef = React.useRef(null);
  const searchRef = React.useRef(null);

  const isYear = options.length > 12;
  const selected = options.find(o => String(o.value) === String(value));
  // Years read "২০১০", but a keyboard types "2010" as readily as "২০১০" — match
  // on Western digits either way.
  const query = toEnDigits(search.trim());
  const filtered = isYear && query
    ? options.filter(o => toEnDigits(o.label).includes(query))
    : options;

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    if (isYear && searchRef.current) searchRef.current.focus();
    const handler = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
      setSearch('');
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, isYear]);

  // Scroll selected year into view
  React.useEffect(() => {
    if (!open || !menuRef.current) return;
    const active = menuRef.current.querySelector('[data-selected="true"]');
    if (active) active.scrollIntoView({ block: 'center' });
  }, [open]);

  const handleSelect = (optValue) => {
    onChange({ target: { value: optValue } });
    setOpen(false);
    setSearch('');
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(v => !v)}
        className={cn(
          'flex items-center gap-1 px-2.5 py-1 rounded-lg border text-sm font-medium transition-colors',
          'border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800',
          'text-zinc-800 dark:text-zinc-100 hover:bg-zinc-50 dark:hover:bg-zinc-700',
          open && 'border-orange-400 ring-2 ring-orange-400/30'
        )}
      >
        {selected?.label ?? value}
        <IconChevronDown size={13} className={cn('text-zinc-400 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <div
          ref={menuRef}
          className={cn(
            'absolute top-full mt-1 z-[800] rounded-xl shadow-xl border',
            'border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900',
            'overflow-hidden',
            isYear ? 'min-w-[110px]' : 'min-w-[130px]'
          )}
        >
          {/* Search — year only */}
          {isYear && (
            <div className="p-2 border-b border-zinc-100 dark:border-zinc-800">
              <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700">
                <IconSearch size={12} className="text-zinc-400 flex-shrink-0" />
                <input
                  ref={searchRef}
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="সার্চ..."
                  className="flex-1 text-xs bg-transparent outline-none text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 w-16"
                />
              </div>
            </div>
          )}

          {/* Options list */}
          <div className="overflow-y-auto max-h-48 py-1">
            {filtered.length === 0 && (
              <p className="text-xs text-zinc-400 text-center py-3">পাওয়া যায়নি</p>
            )}
            {filtered.map(opt => {
              const isSelected = String(opt.value) === String(value);
              return (
                <button
                  key={opt.value}
                  type="button"
                  data-selected={isSelected}
                  disabled={opt.disabled}
                  onClick={() => handleSelect(opt.value)}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors',
                    isSelected
                      ? 'bg-orange-50 dark:bg-orange-950/30 text-orange-600 dark:text-orange-400 font-semibold'
                      : 'text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800',
                    opt.disabled && 'opacity-40 cursor-not-allowed'
                  )}
                >
                  <span className="w-4 flex-shrink-0">
                    {isSelected && <IconCheck size={13} className="text-orange-500" />}
                  </span>
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Calendar ────────────────────────────────────────────────────────────────
export function Calendar({ className, classNames, showOutsideDays = true, locale = bn, formatters, labels, fromYear, toYear, startMonth, endMonth, ...props }) {
  // react-day-picker v9+ dropped `fromYear` / `toYear` in favour of `startMonth` / `endMonth`.
  // Callers still pass years, so translate them; without a range the dropdowns fall back to the
  // last 100 years and can never reach a future date.
  const rangeStart = startMonth ?? (fromYear != null ? new Date(fromYear, 0) : undefined);
  const rangeEnd = endMonth ?? (toYear != null ? new Date(toYear, 11) : undefined);
  // Bengali digits only belong on a Bengali calendar; another locale keeps its
  // own numbers.
  const isBn = locale?.code === 'bn';
  return (
    <DayPicker
      locale={locale}
      formatters={isBn ? { ...BN_FORMATTERS, ...formatters } : formatters}
      labels={isBn ? { ...BN_LABELS, ...labels } : labels}
      showOutsideDays={showOutsideDays}
      startMonth={rangeStart}
      endMonth={rangeEnd}
      className={cn('p-3 select-none', className)}
      classNames={{
        root: 'w-full',
        months: 'flex flex-col gap-4',
        month: 'flex flex-col gap-3',
        month_caption: 'flex justify-center items-center relative h-9',
        caption_label: 'text-sm font-semibold text-zinc-800 dark:text-zinc-100 hidden',
        nav: 'absolute inset-x-0 top-0 flex items-center justify-between pointer-events-none',
        button_previous: cn(
          'pointer-events-auto h-8 w-8 flex items-center justify-center rounded-lg border',
          'border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400',
          'hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors disabled:opacity-30'
        ),
        button_next: cn(
          'pointer-events-auto h-8 w-8 flex items-center justify-center rounded-lg border',
          'border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400',
          'hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors disabled:opacity-30'
        ),
        dropdowns: 'flex items-center gap-1.5',
        dropdown_root: 'relative',
        dropdown: 'hidden', // hidden — we override via components.Dropdown
        month_grid: 'w-full border-collapse mt-1',
        weekdays: 'flex',
        weekday: 'w-9 h-9 flex items-center justify-center text-[0.75rem] font-medium text-zinc-400 dark:text-zinc-500',
        weeks: 'flex flex-col gap-1',
        week: 'flex',
        day: 'w-9 h-9 p-0 flex items-center justify-center',
        day_button: cn(
          'w-9 h-9 flex items-center justify-center rounded-lg text-sm transition-colors cursor-pointer',
          'hover:bg-zinc-100 dark:hover:bg-zinc-800'
        ),
        selected: '!bg-orange-500 !text-white hover:!bg-orange-600 font-semibold rounded-lg',
        today: 'bg-zinc-100 dark:bg-zinc-800 font-semibold text-zinc-900 dark:text-zinc-100',
        outside: 'opacity-30',
        disabled: 'opacity-30 cursor-not-allowed',
        hidden: 'invisible',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) =>
          orientation === 'left'
            ? <IconChevronLeft size={15} />
            : <IconChevronRight size={15} />,
        Dropdown: CalendarDropdown,
      }}
      {...props}
    />
  );
}
